import { z } from "zod";
import { TimeSlot } from "@carry/shared";
import { PublicIdSchema } from "./common";
import { BankAccountType } from "./bank-account";
import { InvoiceItemExcelDetail } from "./invoice-excel";

export const InvoiceStatus = z.enum(["draft", "issued", "sent", "paid", "void"]);
export const InvoiceItemType = z.enum(["fixed", "distance"]);
export const InvoiceTimeSlot = TimeSlot;

export const InvoiceItem = z.object({
  public_id: PublicIdSchema,
  sequence_number: z.number().int(),
  item_type: InvoiceItemType,
  description: z.string(),
  time_slot: InvoiceTimeSlot.nullable(),
  route_stop_public_id: PublicIdSchema.nullable(),
  route_public_id: PublicIdSchema.nullable(),
  /** F29 明細行との紐付け。集約・手入力明細は null。 */
  work_report_entry_public_id: PublicIdSchema.nullable().default(null),
  /** 請求書作成時にF29から取り込んだ元距離。 */
  source_distance_km: z.number().min(0).nullable().default(null),
  /** quantity を請求書内だけで上書きしたか。 */
  billing_distance_overridden: z.boolean().default(false),
  /** 金額を別途手動上書きし、距離変更時の自動再計算を抑止するか。 */
  amount_manual: z.boolean().default(false),
  quantity: z.number().nullable(),
  unit_price: z.number().int().nullable(),
  amount: z.number().int(),
  /** 税率 (%整数)。課税=10/8、立替金 (駐車・高速代) 非課税=0 */
  tax_rate: z.number().int(),
  remarks: z.string().nullable(),
  /** 明細列データ + 金額内訳。F29リンクのdetailed距離行は既定様式でも最小値を保持する */
  excel_detail: InvoiceItemExcelDetail.nullable(),
  created_at: z.iso.datetime({ offset: true }),
});

/** F25 明細変更履歴: 明細1行のスナップショット (before/after 用)。 */
export const InvoiceItemSnapshot = z.object({
  sequence_number: z.number().int(),
  item_type: InvoiceItemType,
  description: z.string(),
  time_slot: InvoiceTimeSlot.nullable(),
  work_report_entry_public_id: PublicIdSchema.nullable().default(null),
  source_distance_km: z.number().min(0).nullable().default(null),
  billing_distance_overridden: z.boolean().default(false),
  amount_manual: z.boolean().default(false),
  quantity: z.number().nullable(),
  unit_price: z.number().int().nullable(),
  amount: z.number().int(),
  tax_rate: z.number().int(),
  remarks: z.string().nullable(),
});

export const InvoiceItemFieldChange = z.object({
  field: z.string(),
  before: z.unknown(),
  after: z.unknown(),
});

/**
 * F25 変更履歴の 1 変更。
 * - `scope='item'`   : 明細行の追加 / 削除 / 変更。`before`/`after` に明細スナップショットが入る。
 * - `scope='header'` : 支払期日・請求先情報・対象月など明細以外の変更 (Issue #339)。
 *   1 操作につき 1 件にまとめ、個々の項目差分は `changed_fields` に入る
 *   (`before`/`after` は null、`sequence_number` は 0)。
 */
export const InvoiceItemChange = z.object({
  public_id: PublicIdSchema,
  /** 1 回の更新操作でまとまった変更のグループ ID。同一操作の変更を UI でまとめられる。 */
  change_group_id: z.string(),
  /** 変更の対象範囲。既存レコードは DB 側の既定値により "item" で返る。 */
  scope: z.enum(["item", "header"]),
  change_type: z.enum(["added", "removed", "updated"]),
  sequence_number: z.number().int(),
  description: z.string(),
  before: InvoiceItemSnapshot.nullable(),
  after: InvoiceItemSnapshot.nullable(),
  changed_fields: z.array(InvoiceItemFieldChange),
  actor_public_id: PublicIdSchema.nullable(),
  actor_name: z.string().nullable(),
  created_at: z.iso.datetime({ offset: true }),
});

export const InvoiceSummary = z.object({
  id: PublicIdSchema,
  invoice_number: z.string(),
  client_public_id: PublicIdSchema,
  client_name: z.string(),
  billing_month: z.string(),
  time_slot: InvoiceTimeSlot.nullable(),
  subtotal: z.number().int(),
  tax_amount: z.number().int(),
  total_amount: z.number().int(),
  total_delivery_count: z.number().int(),
  total_distance_km: z.number().nullable(),
  bank_account_public_id: PublicIdSchema.nullable(),
  bank_account_info: z.string().nullable(),
  /**
   * 請求書 Excel に印字する振込先口座の選択 (Issue #292)。
   * null = 全口座 (口座マスタに追従)。[] は印字対象なし (発行不可)。
   */
  bank_account_public_ids: z.array(PublicIdSchema).nullable(),
  /** 繰越し額 (前月未入金の繰越)。既定 0、手動編集可。今回ご請求額 = 繰越し額 + 当月合計 */
  carryover_amount: z.number().int(),
  due_date: z.iso.date().nullable(),
  status: InvoiceStatus,
  issued_at: z.iso.datetime({ offset: true }).nullable(),
  sent_at: z.iso.datetime({ offset: true }).nullable(),
  paid_at: z.iso.datetime({ offset: true }).nullable(),
  paid_by_public_id: PublicIdSchema.nullable(),
  pdf_path: z.string().nullable(),
  excel_path: z.string().nullable(),
  remarks: z.string().nullable(),
  created_by_public_id: PublicIdSchema,
  created_at: z.iso.datetime({ offset: true }),
  updated_at: z.iso.datetime({ offset: true }),
});

/**
 * 請求書の宛先・発行者ブロック (Issue #266)。
 * issued 遷移時点のスナップショットとして invoices に保存する (証憑がマスタ変更で変わらないように)。
 * draft では未保存 (null) のため、API が現在のマスタ/システム設定から都度解決して返す。
 */
/** 振込先口座のスナップショット (発行時点の bank_accounts を固定) */
export const InvoiceBankAccountSnapshot = z.object({
  bank_name: z.string(),
  branch_name: z.string(),
  account_type: BankAccountType,
  account_number: z.string(),
  account_holder_name: z.string(),
});

export const InvoiceBillingSnapshot = z.object({
  /** 宛名 (clients.billing_name ?? clients.name)。敬称「御中」は出力時に付与する */
  billing_name: z.string(),
  billing_postal_code: z.string().nullable(),
  billing_address: z.string().nullable(),
  billing_department: z.string().nullable(),
  billing_contact_name: z.string().nullable(),
  issuer_name: z.string().nullable(),
  issuer_postal_code: z.string().nullable(),
  issuer_address: z.string().nullable(),
  issuer_tel: z.string().nullable(),
  /** FAX 番号 (Issue #267 で追加)。旧スナップショットには存在しないため optional */
  issuer_fax: z.string().nullable().optional(),
  issuer_registration_number: z.string().nullable(),
  /**
   * 振込先口座リスト (Issue #267: 複数口座印字)。default 口座を先頭に全件。
   * 旧スナップショット (Issue #266 以前) には存在しないため optional。
   * 未設定時は invoices.bank_account_info (単一) にフォールバックする。
   */
  bank_accounts: z.array(InvoiceBankAccountSnapshot).optional(),
});

export const InvoiceDetail = InvoiceSummary.extend({
  items: z.array(InvoiceItem),
  /** 宛先・発行者ブロック。issued 以降は発行時スナップショット、draft は現在のマスタから解決 */
  billing_snapshot: InvoiceBillingSnapshot.nullable(),
  /** 同一取引先の前月 (直近過去月・void 除く) 請求書の税込合計。存在しない場合 null */
  previous_total_amount: z.number().int().nullable(),
  /**
   * 請求先情報 (Issue #297)。作成時に取引先マスタからコピーして請求書側で保持し、
   * draft 中は編集可能。以降のマスタ変更は既存請求書に波及しない。
   * billing_name が null の場合、宛名は取引先名にフォールバックする。
   */
  billing_name: z.string().nullable(),
  billing_postal_code: z.string().nullable(),
  billing_address: z.string().nullable(),
  billing_department: z.string().nullable(),
  billing_contact_name: z.string().nullable(),
  billing_email: z.string().nullable(),
});

export const InvoiceItemInput = z
  .object({
    sequence_number: z.number().int().min(1),
    item_type: InvoiceItemType,
    description: z.string().min(1),
    time_slot: InvoiceTimeSlot.nullable().optional(),
    route_stop_public_id: PublicIdSchema.nullable().optional(),
    route_public_id: PublicIdSchema.nullable().optional(),
    work_report_entry_public_id: PublicIdSchema.nullable().default(null),
    source_distance_km: z.number().min(0).nullable().default(null),
    billing_distance_overridden: z.boolean().default(false),
    amount_manual: z.boolean().default(false),
    quantity: z.number().nullable().optional(),
    unit_price: z.number().int().nullable().optional(),
    amount: z.number().int(),
    /** 税率 (%整数)。未指定時は課税 10% を既定とする */
    tax_rate: z.number().int().optional(),
    remarks: z.string().nullable().optional(),
    /** 明細列データ + 金額内訳。F29リンクのdetailed距離行はサーバーが最小値を補完する */
    excel_detail: InvoiceItemExcelDetail.nullable().optional(),
  })
  .strict();

export const ListInvoicesInput = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  status: InvoiceStatus.optional(),
  billing_month: z.iso.date().optional(),
  client_public_id: PublicIdSchema.optional(),
});

export const CreateInvoiceInput = z
  .object({
    client_public_id: PublicIdSchema,
    billing_month: z.iso.date(),
    time_slot: InvoiceTimeSlot.nullable().optional(),
    due_date: z.iso.date().nullable().optional(),
    /** 印字する振込先口座の選択 (省略時 null = 全口座) */
    bank_account_public_ids: z.array(PublicIdSchema).nullable().optional(),
    /** 繰越し額 (省略時 0) */
    carryover_amount: z.number().int().optional(),
    remarks: z.string().nullable().optional(),
    items: z.array(InvoiceItemInput).min(1),
  })
  .strict();

export const UpdateInvoiceInput = z
  .object({
    billing_month: z.iso.date().optional(),
    time_slot: InvoiceTimeSlot.nullable().optional(),
    due_date: z.iso.date().nullable().optional(),
    /** 印字する振込先口座の選択。null で全口座に戻す */
    bank_account_public_ids: z.array(PublicIdSchema).nullable().optional(),
    /** 繰越し額の手動修正 */
    carryover_amount: z.number().int().optional(),
    /** 請求先情報の draft 中編集 (Issue #297)。取引先マスタは変更しない */
    billing_name: z.string().max(200).nullable().optional(),
    billing_postal_code: z.string().max(20).nullable().optional(),
    billing_address: z.string().max(500).nullable().optional(),
    billing_department: z.string().max(100).nullable().optional(),
    billing_contact_name: z.string().max(100).nullable().optional(),
    billing_email: z.email("メールアドレスの形式で入力してください").nullable().optional(),
    /** 明細再生成で請求書内距離上書きが失われることへの明示確認 */
    confirm_override_loss: z.boolean().optional(),
    /** 業務日報から明細をサーバー再生成する。itemsとの同時指定は不可。 */
    regenerate_items: z.boolean().optional(),
    remarks: z.string().nullable().optional(),
    items: z.array(InvoiceItemInput).optional(),
  })
  .strict()
  .superRefine((input, ctx) => {
    if (input.regenerate_items === true && input.items !== undefined) {
      ctx.addIssue({
        code: "custom",
        path: ["items"],
        message: "明細再生成とitems更新は同時に指定できません",
      });
    }
    if (input.confirm_override_loss === true && input.regenerate_items !== true) {
      ctx.addIssue({
        code: "custom",
        path: ["confirm_override_loss"],
        message: "上書き消失の確認は明細再生成時だけ指定できます",
      });
    }
  });

/**
 * F25 Phase 2: 配送実績→請求書のプレビュー (DB 書込なし).
 * - billing_month は `YYYY-MM` (作成系の `YYYY-MM-DD` とは形式を区別).
 * - 出力 items は DB 永続前の preview なので public_id / created_at は持たない.
 */
export const PreviewInvoiceInput = z.object({
  client_public_id: PublicIdSchema,
  billing_month: z.string().regex(/^\d{4}-\d{2}$/, {
    message: "billing_month は YYYY-MM 形式であること",
  }),
});

export const PreviewInvoiceItem = z.object({
  sequence_number: z.number().int().min(1),
  item_type: InvoiceItemType,
  description: z.string(),
  time_slot: InvoiceTimeSlot.nullable(),
  route_stop_public_id: PublicIdSchema.nullable(),
  route_public_id: PublicIdSchema.nullable(),
  work_report_entry_public_id: PublicIdSchema.nullable().default(null),
  source_distance_km: z.number().min(0).nullable().default(null),
  billing_distance_overridden: z.boolean().default(false),
  amount_manual: z.boolean().default(false),
  quantity: z.number().nullable(),
  unit_price: z.number().int().nullable(),
  amount: z.number().int(),
  /** 税率 (%整数)。課税=10/8、立替金 (駐車・高速代) 非課税=0 */
  tax_rate: z.number().int(),
  remarks: z.string().nullable(),
  /** 明細列データ + 金額内訳。F29リンクのdetailed距離行は既定様式でも最小値を保持する */
  excel_detail: InvoiceItemExcelDetail.nullable(),
});

export const PreviewInvoiceOutput = z.object({
  items: z.array(PreviewInvoiceItem),
  warnings: z.array(z.string()),
  blocking_issues: z.array(
    z.object({
      code: z.literal("billing_area_unresolved"),
      message: z.string(),
      count: z.number().int().min(1),
    }),
  ),
});

export type InvoiceSummaryDto = z.infer<typeof InvoiceSummary>;
export type InvoiceDetailDto = z.infer<typeof InvoiceDetail>;
export type InvoiceBillingSnapshotDto = z.infer<typeof InvoiceBillingSnapshot>;
export type InvoiceBankAccountSnapshotDto = z.infer<typeof InvoiceBankAccountSnapshot>;
export type InvoiceItemDto = z.infer<typeof InvoiceItem>;
export type InvoiceItemChangeDto = z.infer<typeof InvoiceItemChange>;
export type InvoiceItemSnapshotDto = z.infer<typeof InvoiceItemSnapshot>;
export type InvoiceItemInputDto = z.infer<typeof InvoiceItemInput>;
export type ListInvoicesInputDto = z.infer<typeof ListInvoicesInput>;
export type CreateInvoiceInputDto = z.infer<typeof CreateInvoiceInput>;
export type UpdateInvoiceInputDto = z.infer<typeof UpdateInvoiceInput>;
export type PreviewInvoiceInputDto = z.infer<typeof PreviewInvoiceInput>;
export type PreviewInvoiceItemDto = z.infer<typeof PreviewInvoiceItem>;
export type PreviewInvoiceOutputDto = z.infer<typeof PreviewInvoiceOutput>;
